import React, { useState } from 'react';
import TimelineChart from './TimelineChart';
import BrainMap from './BrainMap';

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function TaskSummary({ taskData, taskLabel }) {
  if (!taskData || taskData.error) {
    return (
      <div className="stat-card">
        <div className="label">Task {taskLabel}</div>
        <div className="value" style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>
          {taskData?.error || 'Not available'}
        </div>
      </div>
    );
  }

  const { summary, events, n_windows, model_used, task_name } = taskData;

  return (
    <div className="fade-in">
      <div className="model-info" style={{ marginBottom: 12 }}>
        <span>Task {taskLabel}: {task_name}</span>
        <span>|</span>
        <span>Model: <span className="model-name">{model_used}</span></span>
        <span>|</span>
        <span>{n_windows} windows analyzed</span>
      </div>

      <div className="results-grid">
        {Object.entries(summary).map(([className, stats]) => {
          let cardClass = 'stat-card';
          if (className.toLowerCase().includes('seizure')) cardClass += ' seizure';
          else if (className.toLowerCase().includes('ied') || className.toLowerCase().includes('sharp'))
            cardClass += ' ied';
          else if (className.toLowerCase().includes('normal') || className.toLowerCase().includes('interictal'))
            cardClass += ' normal';

          return (
            <div key={className} className={cardClass}>
              <div className="label">{className}</div>
              <div className="value">{stats.percentage}%</div>
              <div className="sub">
                {stats.count} / {n_windows} windows &middot; Mean prob: {stats.mean_prob}
              </div>
            </div>
          );
        })}

        {events && events.length > 0 && (
          <div className="stat-card seizure">
            <div className="label">Events Detected</div>
            <div className="value">{events.length}</div>
            <div className="sub">
              {taskLabel === 'A' ? 'Seizure episodes' : 'IED episodes'}
            </div>
          </div>
        )}
      </div>

      {/* Events Table */}
      {events && events.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: '0.95rem', marginBottom: 10, color: 'var(--text-secondary)' }}>
            Detected Events
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="events-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Probability</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <span className={`badge ${evt.type === 'seizure' ? 'badge-seizure' : 'badge-ied'}`}>
                        {evt.type.toUpperCase()}
                      </span>
                    </td>
                    <td>{formatTime(evt.start_sec)}</td>
                    <td>{formatTime(evt.end_sec)}</td>
                    <td>{(evt.probability * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ResultsPanel({ results }) {
  const [activeTab, setActiveTab] = useState(null);

  if (!results) return null;

  const taskLabels = Object.keys(results.tasks || {});
  const currentTab = activeTab || taskLabels[0] || 'A';

  const allTabs = [
    ...taskLabels.map((t) => ({ key: t, label: `Task ${t}` })),
    { key: 'timeline', label: 'Timeline' },
    ...(results.brain_map ? [{ key: 'brainmap', label: 'Brain Map' }] : []),
  ];

  return (
    <div className="card fade-in">
      <div className="card-header">
        <span className="icon">📊</span>
        <h2>Analysis Results</h2>
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {results.filename} &middot; {results.duration_sec}s &middot;
          {results.n_channels}ch &middot; {results.processing_time_sec}s
        </span>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {allTabs.map(({ key, label }) => (
          <button
            key={key}
            className={`tab ${currentTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {taskLabels.includes(currentTab) && (
        <TaskSummary
          taskData={results.tasks[currentTab]}
          taskLabel={currentTab}
        />
      )}

      {currentTab === 'timeline' && (
        <TimelineChart results={results} />
      )}

      {currentTab === 'brainmap' && results.brain_map && (
        <BrainMap data={results.brain_map} />
      )}
    </div>
  );
}
